import React from 'react';
import PropTypes from 'prop-types';

import If from '../../../../shared/If';
import Box from '../../../../shared/Box';
import Image from '../../../../shared/Image';
import Input from '../../../../shared/Input';
import { LinkText } from '../../../../shared/Link';
import Text from '../../../../shared/Text';
import Icon from '../../../../shared/Icon';
import Mask from '../../../../shared/Mask';
import SceneHeading from '../SceneHeading';
import Slider from '../../../../shared/Slider';
import Button from '../../../../shared/Button';
import Spinner from '../../../../shared/Spinner';
import Pressable from '../../../../shared/Pressable';
import Hoverable from '../../../../shared/Hoverable';
import InfiniteScroll from '../../../../shared/InfiniteScroll';
import ImageResizerAndCropper from '../../../../shared/ImageResizerAndCropper';

import NoResultsSection from './components/NoResultsSection';
import styles from './ImageSearchScene.sass';

const noop = () => null;

const getImageUrlToCropFromSourceImage = sourceImage => {
  if (sourceImage.maybeUnsplashImage) {
    return sourceImage.maybeUnsplashImage.fullResUrl;
  }
  if (sourceImage.maybeLocalImageBlobUrl) {
    return sourceImage.maybeLocalImageBlobUrl;
  }
  if (sourceImage.maybeHttpImageUrl) {
    return sourceImage.maybeHttpImageUrl;
  }
};

const ImageSearchScene = props => {
  const {
    unsplashImages,
    coverArt,
    searchText,
    onSearchTextChange,
    onRequestMoreItems,
    onChangeZoomLevel,
    zoomLevel,
    xCropPosition,
    yCropPosition,
    onSubmitCroppedPhoto,
    onCropPositionChange,
    onSelectImage,
    onCropComplete,
    isHidingImageSearch,
    isLoadingImageToCrop,
    isButtonDisabled,
    isButtonProcessing,
  } = props;
  return (
    <Box>
      <If
        condition={isHidingImageSearch}
        ifRender={() => <SceneHeading title="Crop your photo" subtitle="" />}
        elseRender={() => (
          <SceneHeading
            title="Search for a photo"
            subtitle="This search is powered by Unsplash, which offers beautiful images completely free!"
          />
        )}
      />
      <Box display="flex" justifyContent="center" wrap>
        <Box
          position="relative"
          smMarginLeft={20}
          smMarginRight={20}
          mdMarginLeft={40}
          mdMarginRight={40}
          lgMarginLeft={40}
          lgMarginRight={40}
          width={300}
        >
          <Box width="100%" height={300} shape="rounded">
            <Mask shape="rounded">
              <If
                condition={isLoadingImageToCrop}
                ifRender={() => (
                  <Box
                    width="100%"
                    height="100%"
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    color="#F4F4F5"
                  >
                    <Spinner size={60} color="#B3B3B4" />
                  </Box>
                )}
                elseRender={() => (
                  <ImageResizerAndCropper
                    imageUrl={getImageUrlToCropFromSourceImage(
                      coverArt.sourceImage
                    )}
                    xPosition={xCropPosition}
                    yPosition={yCropPosition}
                    onPositionChange={onCropPositionChange}
                    width={300}
                    height={300}
                    scale={zoomLevel}
                    onCropComplete={onCropComplete}
                    shape="rounded"
                  />
                )}
              />
            </Mask>
          </Box>
          <If
            condition={
              !isLoadingImageToCrop &&
              coverArt.sourceImage.maybeUnsplashImage &&
              coverArt.sourceImage.maybeUnsplashImage.attributionName
            }
            ifRender={() => (
              <Box>
                <Box padding={5}>
                  <Text align="center" size="sm" color="#7f8287">
                    Photo by{' '}
                    <LinkText
                      isInline
                      to={
                        coverArt.sourceImage.maybeUnsplashImage &&
                        coverArt.sourceImage.maybeUnsplashImage.attributionUrl
                      }
                    >
                      <Text isInline isBold size="sm" color="#7f8287">
                        {coverArt.sourceImage.maybeUnsplashImage &&
                          coverArt.sourceImage.maybeUnsplashImage
                            .attributionName}
                      </Text>{' '}
                    </LinkText>
                    on Unsplash
                  </Text>
                </Box>
              </Box>
            )}
          />
          <If
            condition={!isLoadingImageToCrop}
            ifRender={() => (
              <Box marginTop={10} marginBottom={10}>
                <Slider
                  minValue={1}
                  maxValue={10}
                  step={0.01}
                  value={zoomLevel}
                  onChange={value => {
                    onChangeZoomLevel(value);
                  }}
                />
              </Box>
            )}
          />
          <If
            condition={isHidingImageSearch}
            ifRender={() => (
              <Box display="flex" justifyContent="end">
                <Box width={300} marginTop={50}>
                  <Pressable
                    onPress={isButtonDisabled ? null : onSubmitCroppedPhoto}
                    fullWidth
                  >
                    {({ isPressed }) => (
                      <Hoverable fullWidth>
                        {({ isHovering }) => (
                          <Button
                            isDisabled={isButtonDisabled}
                            isProcessing={isButtonProcessing}
                            isHovered={isHovering}
                            isActive={isPressed}
                            type="unelevated"
                            text="Continue"
                            shape="pill"
                            size="md"
                            isFullWidth
                            colorTheme="primary"
                          />
                        )}
                      </Hoverable>
                    )}
                  </Pressable>
                </Box>
              </Box>
            )}
          />
        </Box>
        <If
          condition={!isHidingImageSearch}
          ifRender={() => (
            <Box
              maxWidth={383}
              smMarginLeft={20}
              smMarginRight={20}
              mdMarginLeft={40}
              mdMarginRight={40}
              lgMarginLeft={40}
              lgMarginRight={40}
              minWidth={300}
              flex="grow"
            >
              <Box>
                <Input
                  backgroundColor="#dfe0e1"
                  shape="pill"
                  value={searchText}
                  onChange={onSearchTextChange}
                  placeholder="Search for any keyword or phrase"
                  renderPrepend={() => (
                    <Box width={12.6} height={12.9}>
                      <Icon type="magnifying_glass" />
                    </Box>
                  )}
                />
              </Box>
              <Box height={270}>
                <If
                  condition={unsplashImages.length === 0}
                  ifRender={() => (
                    <Box heigth={270}>
                      <NoResultsSection />
                    </Box>
                  )}
                  elseRender={() => (
                    <InfiniteScroll
                      items={unsplashImages}
                      onRequestMore={onRequestMoreItems}
                      // height={300}
                      renderInfiniteScrollContent={unsplashImages => (
                        <Box display="flex" justifyContent="center" wrap>
                          {/* TODO: Replace with a Loop component */}
                          {unsplashImages.map(unsplashImage => (
                            <Pressable
                              onPress={() => {
                                onSelectImage(unsplashImage);
                              }}
                            >
                              {({ isPressed }) => (
                                <Hoverable>
                                  {({ isHovering }) => (
                                    <Box
                                      position="relative"
                                      width={63}
                                      height={63}
                                      margin={5}
                                    >
                                      <Box>
                                        <Mask shape="rounded">
                                          <Image
                                            renderLoadingPlaceholder={() => (
                                              <Box
                                                color="#F4F4F5"
                                                width="100%"
                                                height="100%"
                                              />
                                            )}
                                            height={63}
                                            width={63}
                                            imageUrl={
                                              unsplashImage.thumbnailUrl
                                            }
                                            objectFit="cover"
                                          />
                                        </Mask>
                                      </Box>
                                      <If
                                        condition={isHovering || isPressed}
                                        ifRender={() => (
                                          <Box
                                            position="absolute"
                                            top
                                            left
                                            width="100%"
                                            height="100%"
                                            display="flex"
                                            justifyContent="center"
                                            alignItems="center"
                                            color={`rgba(41,47,54, ${
                                              isPressed
                                                ? '.20'
                                                : isHovering
                                                ? '.15'
                                                : '.0'
                                            })`}
                                            shape="rounded"
                                          />
                                        )}
                                      />
                                      <If
                                        condition={
                                          coverArt.sourceImage
                                            .maybeUnsplashImage &&
                                          coverArt.sourceImage
                                            .maybeUnsplashImage.thirdPartyId ===
                                            unsplashImage.thirdPartyId
                                        }
                                        ifRender={() => (
                                          <Box
                                            position="absolute"
                                            top
                                            left
                                            width="100%"
                                            height="100%"
                                            display="flex"
                                            justifyContent="center"
                                            alignItems="center"
                                          >
                                            <Box display="inline-block">
                                              <Box
                                                // shape="circle"
                                                // color="rgba(255,255,255, .4)"
                                                height={37}
                                                width={37}
                                                display="flex"
                                                justifyContent="center"
                                                alignItems="center"
                                              >
                                                <Box width={24}>
                                                  <Icon
                                                    type="checkmark"
                                                    fillColor="white"
                                                  />
                                                </Box>
                                              </Box>
                                            </Box>
                                          </Box>
                                        )}
                                      />
                                    </Box>
                                  )}
                                </Hoverable>
                              )}
                            </Pressable>
                          ))}
                        </Box>
                      )}
                    />
                  )}
                />
                <If
                  condition={unsplashImages.length !== 0}
                  ifRender={() => (
                    <div
                      style={{
                        width: '100%',
                        height: 2.7,
                        opacity: 0.1,
                        backgroundImage:
                          'linear-gradient(to right, #ffffff, #000000 49%, #ffffff)',
                      }}
                    />
                  )}
                />
              </Box>
              <Box display="flex" justifyContent="end">
                <Box width={300} marginTop={104}>
                  <Pressable
                    onPress={isButtonDisabled ? null : onSubmitCroppedPhoto}
                    fullWidth
                  >
                    {({ isPressed }) => (
                      <Hoverable fullWidth>
                        {({ isHovering }) => (
                          <Button
                            isDisabled={isButtonDisabled}
                            isProcessing={isButtonProcessing}
                            isHovered={isHovering}
                            isActive={isPressed}
                            type="unelevated"
                            text="Continue"
                            shape="pill"
                            size="md"
                            isFullWidth
                            colorTheme="primary"
                          />
                        )}
                      </Hoverable>
                    )}
                  </Pressable>
                </Box>
              </Box>
            </Box>
          )}
        />
      </Box>
    </Box>
  );
};

const unSplashImagePropType = PropTypes.shape({
  thumbnailUrl: PropTypes.string.isRequired,
  fullResUrl: PropTypes.string.isRequired,
  id: PropTypes.string,
  authorName: PropTypes.string,
});

const coverArtPropType = PropTypes.shape({
  maybeCroppedImageUrl: PropTypes.string,
  maybeFinalImageUrl: PropTypes.string,
  sourceImage: {
    maybeUnsplashImage: unSplashImagePropType,
    maybeLocalImageBlobUrl: PropTypes.string,
    maybeHttpImageUrl: PropTypes.string,
  },
});

ImageSearchScene.propTypes = {
  imageUrlToCrop: PropTypes.string,
  unsplashImages: PropTypes.arrayOf(unSplashImagePropType),
  searchText: PropTypes.string,
  onSearchTextChange: PropTypes.func,
  onRequestMoreItems: PropTypes.func,
  onChangeZoomLevel: PropTypes.func,
  zoomLevel: PropTypes.number,
  xCropPosition: PropTypes.number,
  yCropPosition: PropTypes.number,
  onSubmitCroppedPhoto: PropTypes.func,
  onCropPositionChange: PropTypes.func,
  onSelectImage: PropTypes.func,
  coverArt: coverArtPropType.isRequired,
  onCropComplete: PropTypes.func,
  isHidingImageSearch: PropTypes.bool,
  isLoadingImageToCrop: PropTypes.bool,
  isButtonDisabled: PropTypes.bool,
  isButtonProcessing: PropTypes.bool,
};

ImageSearchScene.defaultProps = {
  imageUrlToCrop: '',
  unsplashImages: [],
  searchText: '',
  zoomLevel: 1,
  xCropPosition: 0,
  yCropPosition: 0,
  onSearchTextChange: noop,
  onRequestMoreItems: noop,
  onChangeZoomLevel: noop,
  onSubmitCroppedPhoto: noop,
  onCropPositionChange: noop,
  onSelectImage: noop,
  onCropComplete: noop,
  isHidingImageSearch: false,
  isLoadingImageToCrop: false,
  isButtonDisabled: false,
  isButtonProcessing: false,
};

export default ImageSearchScene;
